import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, User, CalendarDays, Receipt } from "lucide-react";
import api from "@/lib/api";
import { apiErr } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchSelect from "@/components/op/SearchSelect";

const num = (n) => new Intl.NumberFormat("id-ID").format(n || 0);
const fmtDate = (s) => {
  if (!s) return "-";
  try { return new Intl.DateTimeFormat("id-ID", { dateStyle: "long" }).format(new Date(s)); } catch { return s; }
};

export default function CorrectionDialog({ opNo, module, onClose, onDone }) {
  const [action, setAction] = useState("KOREKSI");
  const [orig, setOrig] = useState(null);
  const [items, setItems] = useState([]);   // PENGAMBILAN barang
  const [tools, setTools] = useState([]);    // PENGAMBILAN alat
  const [setorItems, setSetorItems] = useState([]); // SETORAN
  const [rijekItems, setRijekItems] = useState([]); // RIJEK_SUSULAN
  const [reason, setReason] = useState("");
  const [products, setProducts] = useState([]);
  const [bon, setBon] = useState([]);
  const [units, setUnits] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [workerId, setWorkerId] = useState("");
  const [tdate, setTdate] = useState("");
  const [saving, setSaving] = useState(false);
  const isSetor = module === "SETORAN";
  const isRijek = module === "RIJEK_SUSULAN";

  useEffect(() => {
    api.get(`/op/receipt/${opNo}`).then((r) => {
      const d = r.data; setOrig(d);
      setWorkerId(d.worker_id || "");
      setTdate(d.date || "");
      const trx = d.transactions || [];
      if (module === "SETORAN") {
        setSetorItems(trx.filter((t) => t.transaction_type === "SETOR").map((t) => ({
          product_id: t.product_id, quantity: String(t.quantity ?? ""),
          reject: String((t.repair_quantity || 0) || (t.reject_quantity || 0)),
          repairable: !((t.reject_quantity || 0) > 0),
          keterangan: t.notes || "",
        })));
      } else if (module === "RIJEK_SUSULAN") {
        setRijekItems(trx.filter((t) => t.transaction_type === "RIJEK").map((t) => ({
          product_id: t.product_id, quantity: String(t.quantity ?? ""), repairable: !((t.reject_quantity || 0) > 0), keterangan: t.notes || "",
        })));
      } else {
        setItems(trx.filter((t) => t.transaction_type === "AMBIL").map((t) => ({
          ref_no: t.ref_no || "", product_id: t.product_id, quantity: String(t.quantity ?? ""),
        })));
        setTools(trx.filter((t) => t.transaction_type === "BON").map((t) => ({
          bon_item_id: t.bon_item_id, quantity: String(t.quantity ?? ""), unit: t.unit || "PCS",
        })));
      }
    }).catch(() => toast.error("Gagal memuat struk"));
    api.get("/op/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/op/workers").then((r) => setWorkers(r.data)).catch(() => {});
    if (module === "PENGAMBILAN") {
      api.get("/op/bon-items").then((r) => setBon(r.data)).catch(() => {});
      api.get("/op/units").then((r) => setUnits(r.data)).catch(() => {});
    }
  }, [opNo, module]);

  const pName = (id) => products.find((p) => p.product_id === id)?.product_name || id;
  const bName = (id) => bon.find((b) => b.id === id)?.item_name || orig?.transactions?.find((t) => t.bon_item_id === id)?.bon_item_name || id;
  const workerLabel = (w) => `${w.worker_name || w.worker_id} (${w.worker_id})`;

  const submit = async () => {
    if (!reason.trim()) return toast.error("Alasan wajib diisi");
    const payload = { op_no: opNo, action, reason };
    if (action === "KOREKSI") {
      if (!workerId) return toast.error("Pengepul wajib dipilih");
      payload.worker_id = workerId;
      payload.transaction_date = tdate || null;
      if (isSetor) {
        const v = [];
        for (const it of setorItems) {
          if (!it.product_id || !(Number(it.quantity) > 0)) continue;
          const rj = Number(it.reject || 0);
          if (rj > Number(it.quantity)) return toast.error("Rijek melebihi jumlah setor");
          if (rj > 0 && !String(it.keterangan).trim()) return toast.error("Keterangan rijek wajib diisi");
          v.push({ product_id: it.product_id, quantity: Number(it.quantity), reject: rj, repairable: it.repairable !== false, keterangan: it.keterangan || null });
        }
        if (!v.length) return toast.error("Isi minimal satu barang setor");
        payload.setor_items = v;
      } else if (isRijek) {
        const v = [];
        for (const it of rijekItems) {
          if (!it.product_id || !(Number(it.quantity) > 0)) continue;
          if (!String(it.keterangan).trim()) return toast.error("Keterangan rijek wajib diisi");
          v.push({ product_id: it.product_id, quantity: Number(it.quantity), repairable: it.repairable !== false, keterangan: it.keterangan || null });
        }
        if (!v.length) return toast.error("Isi minimal satu barang rijek");
        payload.rijek_items = v;
      } else {
        const vI = items.filter((x) => x.product_id && Number(x.quantity) > 0)
          .map((x) => ({ ref_no: x.ref_no || null, product_id: x.product_id, quantity: Number(x.quantity) }));
        const vT = tools.filter((x) => x.bon_item_id && Number(x.quantity) > 0)
          .map((x) => ({ bon_item_id: x.bon_item_id, quantity: Number(x.quantity), unit: x.unit }));
        if (!vI.length && !vT.length) return toast.error("Isi minimal satu barang atau alat");
        payload.items = vI; payload.tools = vT;
      }
    }
    setSaving(true);
    try {
      const { data } = await api.post("/op/corrections", payload);
      toast.success(`Permohonan terkirim · ${data.request_id}`);
      onDone && onDone();
      onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const setItem = (i, k, v) => setItems((a) => a.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const setTool = (i, k, v) => setTools((a) => a.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const setSI = (i, k, v) => setSetorItems((a) => a.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const setRI = (i, k, v) => setRijekItems((a) => a.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center overflow-y-auto p-3" data-testid="correction-dialog">
      <div className="bg-white rounded-2xl w-full max-w-3xl my-4">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-semibold text-slate-800">Ajukan Koreksi / Pembatalan</div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100" data-testid="correction-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Info ringkas struk */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="correction-info">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="flex items-start gap-2">
                <Receipt className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <div className="text-[11px] font-bold text-slate-400 uppercase">No. Struk</div>
                  <div className="text-sm font-semibold text-slate-800" data-testid="correction-info-opno">{opNo}</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CalendarDays className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <div className="text-[11px] font-bold text-slate-400 uppercase">Tanggal</div>
                  <div className="text-sm font-semibold text-slate-800" data-testid="correction-info-date">{fmtDate(orig?.date)}</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <User className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <div className="text-[11px] font-bold text-slate-400 uppercase">Pengepul</div>
                  <div className="text-sm font-semibold text-slate-800" data-testid="correction-info-worker">
                    {orig?.worker_name || orig?.worker_id || "-"}{orig?.worker_id ? ` · ${orig.worker_id}` : ""}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={() => setAction("KOREKSI")} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${action === "KOREKSI" ? "bg-[#0071E3] text-white" : "bg-slate-100 text-slate-600"}`} data-testid="correction-tab-koreksi">Koreksi</button>
            <button onClick={() => setAction("PEMBATALAN")} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${action === "PEMBATALAN" ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-600"}`} data-testid="correction-tab-pembatalan">Pembatalan</button>
          </div>

          {action === "PEMBATALAN" ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="correction-cancel-note">
              Seluruh transaksi dalam struk <b>{opNo}</b> akan dibatalkan setelah disetujui admin. Pastikan alasan jelas.
            </div>
          ) : (
            <>
              {/* Pengepul & tanggal (bisa diubah saat koreksi) */}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Pengepul</Label>
                  <SearchSelect value={workerId} onValueChange={setWorkerId}
                    options={workers.map((w) => ({ value: w.worker_id, label: workerLabel(w) }))}
                    placeholder="Pilih pengepul" testId="corr-worker" className="h-10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Tanggal</Label>
                  <Input type="date" value={tdate} onChange={(e) => setTdate(e.target.value)} className="h-10 mt-1" data-testid="corr-date" />
                </div>
              </div>

              {/* DATA LAMA */}
              <div>
                <div className="text-xs font-bold text-slate-500 mb-2">DATA LAMA</div>
                <div className="rounded-xl border border-slate-200 divide-y text-sm" data-testid="correction-old">
                  {(orig?.transactions || []).map((t, i) => (
                    <div key={i} className="px-3 py-2">
                      {isSetor ? (
                        <div>{pName(t.product_id)} · Setor {num(t.quantity)} · Rijek {num(t.repair_quantity || 0)} · Bagus {num(t.good_quantity)}</div>
                      ) : t.transaction_type === "RIJEK" ? (
                        <div>{pName(t.product_id)} · Rijek {num(t.quantity)}{t.notes ? ` · ${t.notes}` : ""}</div>
                      ) : t.transaction_type === "AMBIL" ? (
                        <div>{t.ref_no ? `[${t.ref_no}] ` : ""}{pName(t.product_id)} · {num(t.quantity)} PCS</div>
                      ) : (
                        <div>Alat: {t.bon_item_name || bName(t.bon_item_id)} · {num(t.quantity)} {t.unit}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* USULAN BARU — full width */}
              <div>
                <div className="text-xs font-bold text-[#0071E3] mb-2">USULAN BARU</div>
                {isSetor ? (
                  <div className="space-y-2" data-testid="correction-new-setor">
                    <div className="grid grid-cols-12 gap-2 px-1 text-[11px] font-bold text-slate-400 uppercase">
                      <div className="col-span-5">Barang</div>
                      <div className="col-span-3">Setor</div>
                      <div className="col-span-3">Rijek</div>
                      <div className="col-span-1"></div>
                    </div>
                    {setorItems.map((it, i) => (
                      <div key={i} className="rounded-xl border border-slate-200 p-2 space-y-2">
                        <div className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-5"><SearchSelect value={it.product_id} onValueChange={(v) => setSI(i, "product_id", v)} options={products.map((p) => ({ value: p.product_id, label: p.product_name }))} placeholder="Barang" testId={`corr-setor-product-${i}`} className="h-10" /></div>
                          <div className="col-span-3"><Input type="number" inputMode="numeric" value={it.quantity} onChange={(e) => setSI(i, "quantity", e.target.value)} className="h-10" data-testid={`corr-setor-qty-${i}`} /></div>
                          <div className="col-span-3"><Input type="number" inputMode="numeric" value={it.reject} onChange={(e) => setSI(i, "reject", e.target.value)} className="h-10" data-testid={`corr-setor-reject-${i}`} /></div>
                          <div className="col-span-1 flex justify-center"><button onClick={() => setSetorItems((a) => a.filter((_, j) => j !== i))} className="p-2 text-rose-500" data-testid={`corr-setor-del-${i}`}><Trash2 className="w-4 h-4" /></button></div>
                        </div>
                        {Number(it.reject) > 0 && (
                          <div className="flex flex-col sm:flex-row gap-2">
                            <div className="flex rounded-lg bg-slate-100 p-0.5 text-[11px] font-semibold shrink-0" data-testid={`corr-setor-type-${i}`}>
                              <button type="button" onClick={() => setSI(i, "repairable", true)} className={`px-2 py-1 rounded-md ${it.repairable !== false ? "bg-white shadow text-emerald-700" : "text-slate-500"}`} data-testid={`corr-setor-rep-${i}`}>Bisa diperbaiki</button>
                              <button type="button" onClick={() => setSI(i, "repairable", false)} className={`px-2 py-1 rounded-md ${it.repairable === false ? "bg-white shadow text-rose-700" : "text-slate-500"}`} data-testid={`corr-setor-afkir-${i}`}>Afkir</button>
                            </div>
                            <Input placeholder="Keterangan rijek (wajib)" value={it.keterangan} onChange={(e) => setSI(i, "keterangan", e.target.value)} className="h-10" data-testid={`corr-setor-note-${i}`} />
                          </div>
                        )}
                      </div>
                    ))}
                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => setSetorItems((a) => [...a, { product_id: "", quantity: "", reject: "", keterangan: "" }])} data-testid="corr-add-setor"><Plus className="w-4 h-4 mr-1" />Tambah</Button>
                  </div>
                ) : isRijek ? (
                  <div className="space-y-2" data-testid="correction-new-rijek">
                    <div className="grid grid-cols-12 gap-2 px-1 text-[11px] font-bold text-slate-400 uppercase">
                      <div className="col-span-7">Barang</div>
                      <div className="col-span-4">Jml Rijek</div>
                      <div className="col-span-1"></div>
                    </div>
                    {rijekItems.map((it, i) => (
                      <div key={i} className="rounded-xl border border-slate-200 p-2 space-y-2">
                        <div className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-7"><SearchSelect value={it.product_id} onValueChange={(v) => setRI(i, "product_id", v)} options={products.map((p) => ({ value: p.product_id, label: p.product_name }))} placeholder="Barang" testId={`corr-rijek-product-${i}`} className="h-10" /></div>
                          <div className="col-span-4"><Input type="number" inputMode="numeric" value={it.quantity} onChange={(e) => setRI(i, "quantity", e.target.value)} className="h-10" data-testid={`corr-rijek-qty-${i}`} /></div>
                          <div className="col-span-1 flex justify-center"><button onClick={() => setRijekItems((a) => a.filter((_, j) => j !== i))} className="p-2 text-rose-500" data-testid={`corr-rijek-del-${i}`}><Trash2 className="w-4 h-4" /></button></div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <div className="flex rounded-lg bg-slate-100 p-0.5 text-[11px] font-semibold shrink-0" data-testid={`corr-rijek-type-${i}`}>
                            <button type="button" onClick={() => setRI(i, "repairable", true)} className={`px-2 py-1 rounded-md ${it.repairable !== false ? "bg-white shadow text-emerald-700" : "text-slate-500"}`} data-testid={`corr-rijek-rep-${i}`}>Bisa diperbaiki</button>
                            <button type="button" onClick={() => setRI(i, "repairable", false)} className={`px-2 py-1 rounded-md ${it.repairable === false ? "bg-white shadow text-rose-700" : "text-slate-500"}`} data-testid={`corr-rijek-afkir-${i}`}>Afkir</button>
                          </div>
                          <Input placeholder="Keterangan rijek (wajib)" value={it.keterangan} onChange={(e) => setRI(i, "keterangan", e.target.value)} className="h-10" data-testid={`corr-rijek-note-${i}`} />
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => setRijekItems((a) => [...a, { product_id: "", quantity: "", keterangan: "" }])} data-testid="corr-add-rijek"><Plus className="w-4 h-4 mr-1" />Tambah</Button>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="correction-new-ambil">
                    <div className="space-y-2">
                      <div className="grid grid-cols-12 gap-2 px-1 text-[11px] font-bold text-slate-400 uppercase">
                        <div className="col-span-3">No. Nota</div>
                        <div className="col-span-6">Barang</div>
                        <div className="col-span-2">Jumlah</div>
                        <div className="col-span-1"></div>
                      </div>
                      {items.map((it, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-3"><Input placeholder="No." value={it.ref_no} onChange={(e) => setItem(i, "ref_no", e.target.value)} className="h-10" data-testid={`corr-item-refno-${i}`} /></div>
                          <div className="col-span-6"><SearchSelect value={it.product_id} onValueChange={(v) => setItem(i, "product_id", v)} options={products.map((p) => ({ value: p.product_id, label: p.product_name }))} placeholder="Barang" testId={`corr-item-product-${i}`} className="h-10" /></div>
                          <div className="col-span-2"><Input type="number" inputMode="numeric" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} className="h-10" data-testid={`corr-item-qty-${i}`} /></div>
                          <div className="col-span-1 flex justify-center"><button onClick={() => setItems((a) => a.filter((_, j) => j !== i))} className="p-1.5 text-rose-500" data-testid={`corr-item-del-${i}`}><Trash2 className="w-4 h-4" /></button></div>
                        </div>
                      ))}
                      <Button variant="outline" size="sm" className="rounded-full" onClick={() => setItems((a) => [...a, { ref_no: "", product_id: "", quantity: "" }])} data-testid="corr-add-item"><Plus className="w-4 h-4 mr-1" />Tambah Barang</Button>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[11px] font-bold text-slate-400 uppercase">Bon Alat</Label>
                      {tools.map((tl, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-6 text-sm truncate pb-2">{bName(tl.bon_item_id)}</div>
                          <div className="col-span-3"><Input type="number" step="any" inputMode="decimal" placeholder="Jml" value={tl.quantity} onChange={(e) => setTool(i, "quantity", e.target.value)} className="h-10" data-testid={`corr-tool-qty-${i}`} /></div>
                          <div className="col-span-2">
                            <Select value={tl.unit} onValueChange={(v) => setTool(i, "unit", v)}>
                              <SelectTrigger className="h-10" data-testid={`corr-tool-unit-${i}`}><SelectValue /></SelectTrigger>
                              <SelectContent>{units.map((u) => <SelectItem key={u.code} value={u.code}>{u.code}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-1 flex justify-center"><button onClick={() => setTools((a) => a.filter((_, j) => j !== i))} className="p-1.5 text-rose-500" data-testid={`corr-tool-del-${i}`}><Trash2 className="w-4 h-4" /></button></div>
                        </div>
                      ))}
                      <SearchSelect value="" onValueChange={(bid) => setTools((a) => [...a, { bon_item_id: bid, quantity: "", unit: "PCS" }])} options={bon.map((b) => ({ value: b.id, label: b.item_name }))} placeholder="+ Tambah alat" testId="corr-add-tool" className="h-10 rounded-full" />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <div>
            <Label className="text-xs">Alasan (wajib)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Jelaskan alasan koreksi / pembatalan" className="mt-1" data-testid="correction-reason" />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" className="rounded-full" onClick={onClose} data-testid="correction-cancel-btn">Batal</Button>
            <Button disabled={saving} onClick={submit} className="rounded-full bg-[#0071E3]" data-testid="correction-submit">Kirim Permohonan</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
